import { Media, RightType, Territory } from '../static';
import { NodeType } from './node';
import { hasValidConditions, Right, RightVersion } from './right';
import { WaterfallSource } from './waterfall';

/** @see --graph-label-default in builder.scss */
export const defaultColor = '#F3F6FF';

export const SPACING = 32;
const HORIZONTAL_PADDING = 54;
const SOURCE_WIDTH = 300;
const SOURCE_HEIGHT = 70;
const RIGHT_WIDTH = 240;
const RIGHT_HEIGHT = 200;
const TOP_RIGHT_HEIGHT = 308;
const VERTICAL_WIDTH = RIGHT_WIDTH;
const LEVEL_HEIGHT = 148;

export interface Arrow {
  path: string;
  parentId: string;
  childId: string;
  labelPosition: { x: number, y: number };
}

interface NodeBase {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: NodeType;
  children: string[];
  version: Record<string, RightVersion>;
  pools: string[];
  duplicateOf?: string; // Id of duplicated node. Temporary, only used on creation to copy conditions
  // Set once the user has dragged this node: its position then comes from Waterfall.layout
  // rather than from the automatic layout. Display only, see issue #10286.
  manualPosition?: boolean;
  inError?: boolean;
}

function createNodeBase(params: Partial<NodeBase> = {}): NodeBase {
  return {
    id: '',
    name: '',
    x: 0, y: 0,
    width: 0,
    height: 0,
    type: 'right',
    children: [],
    version: {},
    pools: [],
    ...params
  }
}

export interface SourceNode extends NodeBase {
  type: 'source';
  medias: Media[];
  territories: Territory[];
  shadow: boolean;
}

export function createSourceNode(params: Partial<SourceNode> = {}): SourceNode {
  const node = createNodeBase(params);
  return {
    ...node,
    type: 'source',
    width: SOURCE_WIDTH,
    height: SOURCE_HEIGHT,
    medias: [],
    territories: [],
    shadow: false,
    ...params
  }
}

export interface RightNode extends NodeBase {
  type: 'right';
  color: string;
  rightHolderId: string;
  contractId?: string;
  percent: number;
  rightType: RightType;
}

export function createRightNode(params: Partial<RightNode> = {}, isHidden = false, rights: Right[] = []): RightNode {
  const node = createNodeBase(params);
  const rightNode: RightNode = {
    ...node,
    type: 'right',
    width: isHidden ? 0 : RIGHT_WIDTH,
    height: isHidden ? 0 : RIGHT_HEIGHT,
    color: defaultColor,
    rightHolderId: '',
    percent: 0,
    rightType: 'empty',
    ...params
  };

  const right = rights.find(r => r.id === rightNode.id);
  if (right) {
    rightNode.inError = !hasValidConditions(right, rights);
    rightNode.color = !rightNode.inError ? rightNode.color : undefined;
  }

  return rightNode;
}

export interface VerticalNode extends NodeBase {
  type: 'vertical';
  color: string;
  members: RightNode[];
  percent: number;
}

export function createVerticalNode(params: Partial<VerticalNode> = {}): VerticalNode {
  const node = createNodeBase(params);
  const vertical: VerticalNode = {
    ...node,
    type: 'vertical',
    members: [],
    percent: 100,
    height: getVerticalGroupHeight(params.members),
    width: VERTICAL_WIDTH,
    color: defaultColor,
    ...params
  };

  vertical.inError = vertical.members.some(member => member.inError);
  if (vertical.members.length) vertical.color = !vertical.inError ? vertical.members[0].color : undefined;

  return vertical;
}

export function getVerticalGroupHeight(members: RightNode[] = []) {
  return TOP_RIGHT_HEIGHT + (LEVEL_HEIGHT * (members.length - 1));
}

export interface HorizontalNode extends NodeBase {
  type: 'horizontal';
  members: (VerticalNode | RightNode)[];
  percent: number;
  blameId: string;
  collapsed: boolean;
}

export function createHorizontalNode(params: Partial<HorizontalNode> = {}): HorizontalNode {
  const node = createNodeBase(params);
  const members = params.members || [];
  return {
    ...node,
    type: 'horizontal',
    members: [],
    height: members.length > 0 && !params?.collapsed ? (Math.max(...members.map(m => m.height)) + (SPACING * 2)) : RIGHT_HEIGHT,
    width: members.length > 0 && !params?.collapsed ? getHorizontalGroupWidth(members) : RIGHT_WIDTH,
    percent: 100,
    blameId: '',
    collapsed: false,
    ...params
  }
}

function getHorizontalGroupWidth(members: (RightNode | VerticalNode)[] = []) {
  const nonHiddenRightMemberCount = members.filter(m => m.width !== 0 && m.height !== 0 && m.type === 'right').length;
  const nonHiddenVerticalMemberCount = members.filter(m => m.width !== 0 && m.height !== 0 && m.type === 'vertical').length;
  const nonHiddenMemberCount = nonHiddenRightMemberCount + nonHiddenVerticalMemberCount;
  const rightsWidth = RIGHT_WIDTH * nonHiddenRightMemberCount;
  const verticalWidth = VERTICAL_WIDTH * nonHiddenVerticalMemberCount;
  const spacing = SPACING * (nonHiddenMemberCount + 1) + (2 * (HORIZONTAL_PADDING - SPACING));
  return rightsWidth + verticalWidth + spacing;
}

export type Node = SourceNode | RightNode | VerticalNode | HorizontalNode;

interface GraphItems { rights: Right[]; sources: WaterfallSource[] }
export interface GraphDiff { created: GraphItems; updated: GraphItems; deleted: GraphItems }
export function computeDiff(oldGraph: GraphItems, newGraph: GraphItems): GraphDiff {

  const createdRights = newGraph.rights.filter(right => !oldGraph.rights.some(r => r.id === right.id));
  const updatedRights = newGraph.rights
    .filter(right => oldGraph.rights.some(r => r.id === right.id))
    .map(right => ({ ...right, collapsed: oldGraph.rights.find(r => r.id === right.id).collapsed }));
  const deletedRights = oldGraph.rights.filter(right => !newGraph.rights.some(r => r.id === right.id));

  const createdSources = newGraph.sources.filter(source => !oldGraph.sources.some(s => s.id === source.id));
  const updatedSources = newGraph.sources.filter(source => oldGraph.sources.some(s => s.id === source.id));
  const deletedSources = oldGraph.sources.filter(source => !newGraph.sources.some(s => s.id === source.id));

  return {
    created: { rights: createdRights, sources: createdSources },
    updated: { rights: updatedRights, sources: updatedSources },
    deleted: { rights: deletedRights, sources: deletedSources },
  };
}

export function findRightNode(nodeId: string, step = 0, graph: Node[] = [], findStep = true) {
  let node = graph.find(n => n.id === nodeId);
  if (!node) {
    const horizontal = graph.filter(n => n.type === 'horizontal') as HorizontalNode[];
    for (const h of horizontal) {
      node = h.members.find(m => m.id === nodeId);
      if (!node) continue;
      if (node && node.type !== 'vertical') break;
      node = findStep ? (node as VerticalNode).members[step] : node;
      break;
    }
  } else if (node.type === 'vertical' && findStep) {
    node = node.members[step];
  }

  return node as RightNode;
}

/**
 * Get all rightholder ids from a graph
 * @param nodes - The graph nodes
 * @returns An array of unique rightholder ids
 */
export function getRightholderIds(nodes: Node[]) {
  const rightholderIds: string[] = [];
  nodes.forEach(node => {
    if (node.type === 'right') {
      rightholderIds.push(node.rightHolderId);
    } else if (node.type === 'vertical') {
      node.members.forEach(member => {
        if (member.type === 'right') {
          rightholderIds.push(member.rightHolderId);
        }
      });
    } else if (node.type === 'horizontal') {
      node.members.forEach(member => {
        if (member.type === 'right') {
          rightholderIds.push(member.rightHolderId);
        } else if (member.type === 'vertical') {
          member.members.forEach(subMember => {
            if (subMember.type === 'right') {
              rightholderIds.push(subMember.rightHolderId);
            }
          });
        }
      });
    }
  });

  return Array.from(new Set(rightholderIds));
}

export function getRightholderNodes(rightholderId: string, nodes: Node[]) {
  const rightholderNodes: Node[] = [];
  nodes.forEach(node => {
    if (node.type === 'right' && node.rightHolderId === rightholderId) {
      rightholderNodes.push(node);
    } else if (node.type === 'vertical') {
      node.members.forEach(member => {
        if (member.type === 'right' && member.rightHolderId === rightholderId) {
          rightholderNodes.push(member);
        }
      });
    }
    if (node.type === 'horizontal') {
      node.members.forEach(member => {
        if (member.type === 'right' && member.rightHolderId === rightholderId) {
          rightholderNodes.push(member);
        } else if (member.type === 'vertical') {
          member.members.forEach(subMember => {
            if (subMember.type === 'right' && subMember.rightHolderId === rightholderId) {
              rightholderNodes.push(subMember);
            }
          });
        }
      });
    }
  });
  return rightholderNodes;
}